<script setup>
import { reactive, computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import MailingListsAPI from 'dashboard/api/mailingLists';
import EmailMarketingTemplatesAPI from 'dashboard/api/emailMarketingTemplates';
import ContactAPI from 'dashboard/api/contacts';
import CampaignsAPI from 'dashboard/api/campaigns';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import EmailMarketingTemplateSelector from 'dashboard/components-next/Campaigns/Pages/EmailMarketingTemplates/EmailMarketingTemplateSelector.vue';
import LiquidVariableAutocomplete from 'dashboard/components-next/Campaigns/Pages/EmailMarketingTemplates/LiquidVariableAutocomplete.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getEmailInboxes'),
  contactAttributes: useMapGetter('attributes/getContactAttributes'),
  currentUser: useMapGetter('getCurrentUser'),
};

const initialState = {
  title: '',
  subject: '',
  message: '',
  htmlContent: '',
  signature: '',
  emailMarketingTemplateId: null,
  senderName: '',
  logoUrl: '',
  buttonText: '',
  buttonUrl: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  utmContent: '',
  emailCampaignKind: 'transactional',
  inboxId: null,
  scheduledAt: null,
  selectedMailingLists: [],
  selectedContacts: [],
  selectedLabels: [],
  attributeKey: '',
  attributeOperator: 'equal_to',
  attributeValues: '',
  attributeSelectedValues: [],
  attachments: [],
};

const state = reactive({ ...initialState });
const mailingLists = ref([]);
const contactSearchOptions = ref([]);
const selectedContactOptions = ref([]);
const audiencePreviewContacts = ref([]);
const selectedPreviewContactIds = ref([]);
const audiencePreviewTotalCount = ref(0);
const audiencePreviewLimit = ref(0);
const isAudiencePreviewTruncated = ref(false);
const isAudiencePreviewLoaded = ref(false);
const audienceConfirmationToken = ref('');
const previewError = ref('');
const isSearchingContacts = ref(false);
const isLoadingAudiencePreview = ref(false);
const showVariablePicker = ref(false);
const selectedVariableToken = ref('');
const emailMarketingTemplates = ref([]);
const isLoadingEmailMarketingTemplates = ref(false);
const emailMarketingTemplatesError = ref('');
const attachmentError = ref('');
const attachmentInput = ref(null);
let contactSearchController = null;

const MAX_ATTACHMENT_COUNT = 15;
const MAX_ATTACHMENT_TOTAL_SIZE = 14 * 1024 * 1024;

const normalizeAttributeValue = value => String(value).trim();

const attributeTypeByIndex = [
  'text',
  'number',
  'currency',
  'percent',
  'link',
  'date',
  'list',
  'checkbox',
];

const attributeValue = (attribute, camelKey, snakeKey) =>
  attribute?.[camelKey] ?? attribute?.[snakeKey];

const normalizedAttributeType = attribute => {
  const rawType = attributeValue(
    attribute,
    'attributeDisplayType',
    'attribute_display_type'
  );

  const type =
    typeof rawType === 'number'
      ? attributeTypeByIndex[rawType]
      : rawType?.toString().toLowerCase();

  if (['boolean', 'bool'].includes(type)) return 'checkbox';
  if (
    [
      'select',
      'dropdown',
      'multi_select',
      'multi-select',
      'multi select',
    ].includes(type)
  ) {
    return 'list';
  }

  return type || 'text';
};

const normalizedAttributeValues = attribute => {
  const rawValues =
    attributeValue(attribute, 'attributeValues', 'attribute_values') || [];

  if (Array.isArray(rawValues)) return rawValues;

  return rawValues
    .toString()
    .split(/[\n,]/)
    .map(value => value.trim())
    .filter(Boolean);
};

const contactAttributeOptions = computed(() => {
  const standardAttributes = [
    {
      value: 'name',
      label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.STANDARD.NAME'),
    },
    {
      value: 'email',
      label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.STANDARD.EMAIL'),
    },
    {
      value: 'phone_number',
      label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.STANDARD.PHONE'),
    },
    {
      value: 'identifier',
      label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.STANDARD.IDENTIFIER'),
    },
  ];

  const customAttributes =
    formState.contactAttributes.value?.map(attribute => ({
      value: attributeValue(attribute, 'attributeKey', 'attribute_key'),
      label:
        attributeValue(
          attribute,
          'attributeDisplayName',
          'attribute_display_name'
        ) || attributeValue(attribute, 'attributeKey', 'attribute_key'),
      attributeDisplayType: normalizedAttributeType(attribute),
      attributeValues: normalizedAttributeValues(attribute),
    })) ?? [];

  return [...standardAttributes, ...customAttributes];
});

const selectedAttributeDefinition = computed(() =>
  contactAttributeOptions.value.find(
    option => option.value === state.attributeKey
  )
);

const selectedAttributeType = computed(
  () => selectedAttributeDefinition.value?.attributeDisplayType || 'text'
);

const isSelectedAttributeList = computed(
  () => selectedAttributeType.value === 'list'
);

const isSelectedAttributeCheckbox = computed(
  () => selectedAttributeType.value === 'checkbox'
);

const selectedAttributeListOptions = computed(
  () =>
    selectedAttributeDefinition.value?.attributeValues?.map(value => ({
      value,
      label: value,
    })) || []
);

const checkboxAttributeOptions = computed(() => [
  {
    value: 'true',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.BOOLEAN.TRUE'),
  },
  {
    value: 'false',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.BOOLEAN.FALSE'),
  },
]);

const parsedAttributeValues = () => {
  if (state.attributeOperator === 'is_present') return [];

  if (isSelectedAttributeList.value) {
    return state.attributeSelectedValues
      .map(normalizeAttributeValue)
      .filter(Boolean);
  }

  if (isSelectedAttributeCheckbox.value) {
    return state.attributeValues
      ? [normalizeAttributeValue(state.attributeValues)]
      : [];
  }

  return state.attributeValues
    .split(',')
    .map(normalizeAttributeValue)
    .filter(Boolean);
};

const hasResolvedAudiencePreview = () =>
  isAudiencePreviewLoaded.value && selectedPreviewContactIds.value.length > 0;

const hasValidAudience = value => {
  if (hasResolvedAudiencePreview()) return true;

  const hasMailingLists = value.selectedMailingLists?.length > 0;
  if (['marketing', 'newsletter'].includes(value.emailCampaignKind)) {
    return hasMailingLists;
  }

  const hasLabels = value.selectedLabels?.length > 0;
  const hasContacts = value.selectedContacts?.length > 0;
  const hasAttribute =
    value.attributeKey &&
    (value.attributeOperator === 'is_present' ||
      parsedAttributeValues().length > 0);
  return hasContacts || hasLabels || hasAttribute;
};

const utmValues = computed(() => ({
  source: state.utmSource.trim(),
  medium: state.utmMedium.trim(),
  campaign: state.utmCampaign.trim(),
  term: state.utmTerm.trim(),
  content: state.utmContent.trim(),
}));

const hasUtmValues = computed(() =>
  Object.values(utmValues.value).some(Boolean)
);

const hasCompleteUtmValues = computed(
  () =>
    !hasUtmValues.value ||
    (!!utmValues.value.source &&
      !!utmValues.value.medium &&
      !!utmValues.value.campaign)
);

const rules = {
  title: { required, minLength: minLength(1) },
  subject: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  emailCampaignKind: { required },
  selectedMailingLists: {},
  selectedContacts: {},
  selectedLabels: {},
  attributeKey: {},
  attributeValues: {},
  utmSource: {
    complete: helpers.withMessage(
      () => t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_ERROR'),
      () => hasCompleteUtmValues.value
    ),
  },
  utmMedium: {},
  utmCampaign: {},
  audience: {
    valid: helpers.withMessage(
      () => t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.ERROR'),
      () => hasValidAudience(state)
    ),
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const mailingListOptions = computed(() =>
  mapToOptions(
    mailingLists.value.filter(mailingList => mailingList.status === 'active'),
    'id',
    'name'
  )
);

const selectedMailingListDetails = computed(() => {
  const selectedIds = new Set(state.selectedMailingLists.map(Number));
  return mailingLists.value.filter(mailingList =>
    selectedIds.has(Number(mailingList.id))
  );
});

const contactOptions = computed(() => {
  const optionsById = new Map();

  [...selectedContactOptions.value, ...contactSearchOptions.value].forEach(
    option => {
      optionsById.set(option.value, option);
    }
  );

  return [...optionsById.values()];
});

const selectedContactIds = computed({
  get: () => state.selectedContacts,
  set: ids => {
    state.selectedContacts = ids;
    selectedContactOptions.value = contactOptions.value.filter(option =>
      ids.includes(option.value)
    );
  },
});

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const campaignKindOptions = computed(() => [
  {
    value: 'transactional',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.KIND.TRANSACTIONAL'),
  },
  {
    value: 'marketing',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.KIND.MARKETING'),
  },
  {
    value: 'newsletter',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.KIND.NEWSLETTER'),
  },
]);

const availableEmailMarketingTemplates = computed(() =>
  emailMarketingTemplates.value.filter(
    template =>
      template.status === 'active' && template.template_type === 'email'
  )
);

const hasAuthoredTemplateContent = () =>
  [
    state.subject,
    state.message,
    state.htmlContent,
    state.signature,
    state.utmSource,
    state.utmMedium,
    state.utmCampaign,
    state.utmTerm,
    state.utmContent,
  ].some(value => value?.trim());

const clearTemplateSnapshot = () => {
  state.emailMarketingTemplateId = null;
  state.senderName = '';
  state.logoUrl = '';
  state.buttonText = '';
  state.buttonUrl = '';
};

const handleTemplateSelection = templateId => {
  if (!templateId) {
    clearTemplateSnapshot();
    return;
  }

  const template = availableEmailMarketingTemplates.value.find(
    item => String(item.id) === String(templateId)
  );
  if (!template) return;

  if (String(state.emailMarketingTemplateId) === String(template.id)) return;

  if (hasAuthoredTemplateContent()) {
    // eslint-disable-next-line no-alert
    const shouldOverwrite = window.confirm(
      t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.CONFIRM_OVERWRITE')
    );
    if (!shouldOverwrite) return;
  }

  const campaignPayload = template.campaign_payload || {};
  const templateParams = campaignPayload.template_params || {};

  state.subject =
    templateParams.subject ?? campaignPayload.subject ?? template.subject ?? '';
  state.message = campaignPayload.message ?? template.message ?? '';
  state.htmlContent =
    templateParams.html_content ??
    campaignPayload.html_content ??
    template.html_content ??
    '';
  state.signature =
    templateParams.signature ??
    campaignPayload.signature ??
    template.signature ??
    '';
  state.emailMarketingTemplateId = template.id;
  state.senderName =
    templateParams.sender_name ??
    campaignPayload.sender_name ??
    template.sender_name ??
    '';
  state.logoUrl =
    templateParams.logo_url ??
    campaignPayload.logo_url ??
    template.logo_url ??
    '';
  state.buttonText =
    templateParams.button_text ??
    campaignPayload.button_text ??
    template.button_text ??
    '';
  state.buttonUrl =
    templateParams.button_url ??
    campaignPayload.button_url ??
    template.button_url ??
    '';
  const utmParams = templateParams.utm_params || template.metadata?.utm || {};
  state.utmSource = utmParams.source || '';
  state.utmMedium = utmParams.medium || '';
  state.utmCampaign = utmParams.campaign || '';
  state.utmTerm = utmParams.term || '';
  state.utmContent = utmParams.content || '';
};

const campaignVariableKeys = new Set([
  'contact.id',
  'contact.name',
  'contact.first_name',
  'contact.last_name',
  'contact.email',
  'contact.phone',
  'agent.name',
  'agent.first_name',
  'agent.last_name',
  'agent.email',
  'inbox.name',
  'inbox.id',
]);

const variableToken = key => `{{${key}}}`;

const standardVariableLabels = computed(() => ({
  'contact.id': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_ID'),
  'contact.name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_NAME'
  ),
  'contact.first_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_FIRST_NAME'
  ),
  'contact.last_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_LAST_NAME'
  ),
  'contact.email': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_EMAIL'
  ),
  'contact.phone': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.CONTACT_PHONE'
  ),
  'agent.name': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_NAME'),
  'agent.first_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_FIRST_NAME'
  ),
  'agent.last_name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_LAST_NAME'
  ),
  'agent.email': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.AGENT_EMAIL'),
  'inbox.name': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.INBOX_NAME'),
  'inbox.id': t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.INBOX_ID'),
  'account.name': t(
    'CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD.ACCOUNT_NAME'
  ),
}));

const variableLabel = (key, fallback) =>
  standardVariableLabels.value[key] || fallback;

const standardVariableOptions = computed(() => [
  ...MESSAGE_VARIABLES.filter(variable =>
    campaignVariableKeys.has(variable.key)
  ).map(variable => ({
    key: variable.key,
    token: variableToken(variable.key),
    label: variableLabel(variable.key, variable.label),
  })),
  {
    key: 'account.name',
    token: variableToken('account.name'),
    label: variableLabel('account.name', 'Account name'),
  },
]);

const customVariableOptions = computed(
  () =>
    formState.contactAttributes.value
      ?.map(attribute => {
        const attributeKey = attributeValue(
          attribute,
          'attributeKey',
          'attribute_key'
        );
        if (!attributeKey) return null;

        const label =
          attributeValue(
            attribute,
            'attributeDisplayName',
            'attribute_display_name'
          ) || attributeKey;
        const escapedKey = String(attributeKey)
          .replaceAll('\\', '\\\\')
          .replaceAll('"', '\\"');
        const key = `contact.custom_attribute["${escapedKey}"]`;
        return {
          key,
          token: variableToken(key),
          label,
        };
      })
      .filter(Boolean) ?? []
);

const variableGroups = computed(() => [
  {
    key: 'standard',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.STANDARD_GROUP'),
    options: standardVariableOptions.value,
  },
  {
    key: 'custom',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.CUSTOM_GROUP'),
    options: customVariableOptions.value,
  },
]);

const liquidVariableOptions = computed(() =>
  variableGroups.value.flatMap(group => group.options)
);

const selectedVariableOption = computed(() =>
  variableGroups.value
    .flatMap(group => group.options)
    .find(variable => variable.token === selectedVariableToken.value)
);

const insertVariable = token => {
  if (!token) return;

  const separator = state.message && !/\s$/.test(state.message) ? ' ' : '';
  state.message = `${state.message || ''}${separator}${token}`;
  showVariablePicker.value = false;
  selectedVariableToken.value = '';
};

const requiresMailingList = computed(() =>
  ['marketing', 'newsletter'].includes(state.emailCampaignKind)
);

const consentFooterPreview = computed(() =>
  t('CAMPAIGN.EMAIL.CREATE.FORM.CONSENT.PREVIEW')
);

const trackingPreview = computed(() =>
  t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.PREVIEW')
);

const hasAudienceInputs = computed(
  () =>
    state.selectedMailingLists.length > 0 ||
    state.selectedContacts.length > 0 ||
    state.selectedLabels.length > 0 ||
    !!state.attributeKey
);

const hasAudiencePreview = computed(() => isAudiencePreviewLoaded.value);

const requiresAudiencePreview = computed(() => hasAudienceInputs.value);

const canLoadAudiencePreview = computed(
  () => hasAudienceInputs.value && !isLoadingAudiencePreview.value
);

const selectedPreviewCount = computed(
  () => selectedPreviewContactIds.value.length
);

const needsAudienceBuild = computed(
  () => requiresAudiencePreview.value && !hasAudiencePreview.value
);

const isPreviewContactBlocked = contact => {
  if (!requiresMailingList.value) return false;

  return contact.subscription_status !== 'subscribed';
};

const selectablePreviewContactIds = computed(() =>
  audiencePreviewContacts.value
    .filter(contact => !isPreviewContactBlocked(contact))
    .map(contact => contact.id)
);

const blockedPreviewCount = computed(
  () => audiencePreviewContacts.value.filter(isPreviewContactBlocked).length
);

const selectablePreviewCount = computed(
  () => selectablePreviewContactIds.value.length
);

const audiencePreviewSummary = computed(() => {
  const total =
    audiencePreviewTotalCount.value || audiencePreviewContacts.value.length;
  const selected = selectedPreviewContactIds.value.length;

  return t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.SUMMARY', {
    selected,
    total,
  });
});

const audiencePreviewActionLabel = computed(() =>
  hasAudiencePreview.value
    ? t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.RELOAD_BUTTON')
    : t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.BUTTON')
);

const audiencePreviewEligibilitySummary = computed(() =>
  t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.ELIGIBILITY_SUMMARY', {
    available: selectablePreviewCount.value,
    blocked: blockedPreviewCount.value,
  })
);

const defaultOperatorOptions = computed(() => [
  {
    value: 'equal_to',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.OPERATORS.EQUAL_TO'),
  },
  {
    value: 'contains',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.OPERATORS.CONTAINS'),
  },
  {
    value: 'starts_with',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.OPERATORS.STARTS_WITH'),
  },
  {
    value: 'is_present',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.OPERATORS.IS_PRESENT'),
  },
]);

const operatorOptions = computed(() => {
  if (isSelectedAttributeCheckbox.value || isSelectedAttributeList.value) {
    return [defaultOperatorOptions.value[0], defaultOperatorOptions.value[3]];
  }

  return defaultOperatorOptions.value;
});

const getErrorMessage = field => {
  if (!v$.value[field].$error) return '';

  if (field === 'title') return t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.ERROR');
  if (field === 'subject') return t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.ERROR');
  if (field === 'message') return t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.ERROR');
  if (field === 'inboxId') return t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.ERROR');
  if (field === 'scheduledAt') {
    return t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.ERROR');
  }
  return t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.ERROR');
};

const formErrors = computed(() => ({
  title: getErrorMessage('title'),
  subject: getErrorMessage('subject'),
  message: getErrorMessage('message'),
  inbox: getErrorMessage('inboxId'),
  scheduledAt: getErrorMessage('scheduledAt'),
  audience: v$.value.audience.$error
    ? t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.ERROR')
    : '',
}));

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    isLoadingAudiencePreview.value ||
    (hasAudiencePreview.value && selectedPreviewCount.value === 0)
);

const submitButtonLabel = computed(() =>
  needsAudienceBuild.value
    ? t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.LOAD_AUDIENCE')
    : t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CREATE')
);

const isDirty = computed(
  () => JSON.stringify(state) !== JSON.stringify(initialState)
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const handleCancel = () => {
  emit('cancel');
};

const buildAudience = ({ usePreview = true } = {}) => {
  if (usePreview && hasAudiencePreview.value) {
    return selectedPreviewContactIds.value.map(id => ({
      id,
      type: 'Contact',
    }));
  }

  const contactAudience =
    state.selectedContacts?.map(id => ({
      id,
      type: 'Contact',
    })) ?? [];

  const labelAudience =
    state.selectedLabels?.map(id => ({
      id,
      type: 'Label',
    })) ?? [];

  const attributeAudience = state.attributeKey
    ? [
        {
          type: 'ContactAttribute',
          attribute_key: state.attributeKey,
          filter_operator: state.attributeOperator,
          values:
            state.attributeOperator === 'is_present'
              ? []
              : parsedAttributeValues(),
        },
      ]
    : [];

  return [...contactAudience, ...labelAudience, ...attributeAudience];
};

const contactLabel = contact => {
  const details = [contact.email, contact.phone_number].filter(Boolean);
  return details.length
    ? `${contact.name || contact.email || contact.phone_number} (${details.join(', ')})`
    : contact.name || `#${contact.id}`;
};

const mapContactToOption = contact => ({
  value: contact.id,
  label: contactLabel(contact),
});

const searchContacts = async query => {
  const trimmedQuery = query.trim();

  contactSearchController?.abort();

  if (trimmedQuery.length < 2) {
    contactSearchOptions.value = [];
    return;
  }

  const controller = new AbortController();
  contactSearchController = controller;
  isSearchingContacts.value = true;

  try {
    const {
      data: { payload = [] },
    } = await ContactAPI.search(trimmedQuery, 1, 'name', '', {
      signal: controller.signal,
    });

    contactSearchOptions.value = payload
      .filter(contact => contact.email)
      .map(mapContactToOption);
  } catch (error) {
    if (!['AbortError', 'CanceledError'].includes(error?.name)) {
      contactSearchOptions.value = [];
    }
  } finally {
    if (contactSearchController === controller) {
      isSearchingContacts.value = false;
    }
  }
};

const handleAttachmentsChange = event => {
  const files = Array.from(event.target.files || []);
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);

  if (files.length > MAX_ATTACHMENT_COUNT) {
    state.attachments = [];
    attachmentError.value = t(
      'CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.COUNT_ERROR',
      { count: MAX_ATTACHMENT_COUNT }
    );
    event.target.value = '';
    return;
  }

  if (totalSize > MAX_ATTACHMENT_TOTAL_SIZE) {
    state.attachments = [];
    attachmentError.value = t(
      'CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.SIZE_ERROR'
    );
    event.target.value = '';
    return;
  }

  state.attachments = files;
  attachmentError.value = '';
};

const clearAttachments = () => {
  state.attachments = [];
  attachmentError.value = '';
  if (attachmentInput.value) attachmentInput.value.value = '';
};

const buildTemplateParams = subject => {
  const normalizedUtm = Object.fromEntries(
    Object.entries(utmValues.value).filter(([, value]) => value)
  );

  return {
    subject,
    ...(state.htmlContent.trim() && { html_content: state.htmlContent }),
    ...(state.signature.trim() && { signature: state.signature }),
    ...(Object.keys(normalizedUtm).length && { utm_params: normalizedUtm }),
    ...(state.emailMarketingTemplateId && {
      email_marketing_template_id: state.emailMarketingTemplateId,
      sender_name: state.senderName,
      logo_url: state.logoUrl,
      button_text: state.buttonText,
      button_url: state.buttonUrl,
    }),
  };
};

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  sender_id: formState.currentUser.value?.id,
  email_campaign_kind: state.emailCampaignKind,
  template_params: buildTemplateParams(state.subject),
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  mailing_list_ids: state.selectedMailingLists,
  audience: buildAudience(),
  audience_confirmation_token: audienceConfirmationToken.value,
  attachments: state.attachments,
});

const previewPayload = () => ({
  inbox_id: state.inboxId,
  title: state.title || 'Audience preview',
  message: state.message || 'Audience preview',
  email_campaign_kind: state.emailCampaignKind,
  mailing_list_ids: state.selectedMailingLists,
  audience: buildAudience({ usePreview: false }),
  template_params: buildTemplateParams(state.subject || 'Audience preview'),
});

const applyAudiencePreview = payload => {
  const contacts = payload.contacts || [];
  const selectedIds = new Set(payload.selected_contact_ids || []);
  audiencePreviewContacts.value = contacts;
  isAudiencePreviewLoaded.value = true;
  audienceConfirmationToken.value = payload.confirmation_token || '';
  selectedPreviewContactIds.value = contacts
    .filter(
      contact =>
        selectedIds.has(contact.id) && !isPreviewContactBlocked(contact)
    )
    .map(contact => contact.id);
  audiencePreviewTotalCount.value = payload.total_count || contacts.length;
  audiencePreviewLimit.value = payload.limit || contacts.length;
  isAudiencePreviewTruncated.value = !!payload.truncated;
  previewError.value = '';
};

const loadAudiencePreview = async () => {
  previewError.value = '';
  isLoadingAudiencePreview.value = true;

  try {
    const response = await CampaignsAPI.previewAudience(previewPayload());
    applyAudiencePreview(response.data?.payload || {});
  } catch (error) {
    audiencePreviewContacts.value = [];
    selectedPreviewContactIds.value = [];
    isAudiencePreviewLoaded.value = false;
    audienceConfirmationToken.value = '';
    previewError.value =
      error?.response?.data?.message ||
      error?.message ||
      t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.ERROR');
  } finally {
    isLoadingAudiencePreview.value = false;
  }
};

const togglePreviewContact = contactId => {
  const contact = audiencePreviewContacts.value.find(
    item => item.id === contactId
  );
  if (contact && isPreviewContactBlocked(contact)) return;

  const currentIds = new Set(selectedPreviewContactIds.value);
  if (currentIds.has(contactId)) {
    currentIds.delete(contactId);
  } else {
    currentIds.add(contactId);
  }

  selectedPreviewContactIds.value = [...currentIds];
};

const selectAllPreviewContacts = () => {
  selectedPreviewContactIds.value = [...selectablePreviewContactIds.value];
};

const clearPreviewContacts = () => {
  selectedPreviewContactIds.value = [];
};

const resetAudiencePreview = () => {
  audiencePreviewContacts.value = [];
  selectedPreviewContactIds.value = [];
  audiencePreviewTotalCount.value = 0;
  audiencePreviewLimit.value = 0;
  isAudiencePreviewTruncated.value = false;
  isAudiencePreviewLoaded.value = false;
  audienceConfirmationToken.value = '';
  previewError.value = '';
};

const previewStatusLabel = contact => {
  if (!requiresMailingList.value) {
    return t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.STATUS.READY');
  }

  if (contact.subscription_status === 'subscribed') {
    return t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.STATUS.SUBSCRIBED');
  }

  if (contact.subscription_status === 'unsubscribed') {
    return t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.STATUS.UNSUBSCRIBED');
  }

  if (contact.manually_selected) {
    return t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.STATUS.NEEDS_CONSENT');
  }

  return t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.STATUS.NO_CONSENT');
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || attachmentError.value) return;

  if (needsAudienceBuild.value) {
    await loadAudiencePreview();
    return;
  }

  if (requiresAudiencePreview.value && !isAudiencePreviewLoaded.value) {
    previewError.value = t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.REQUIRED');
    return;
  }

  if (
    hasAudiencePreview.value &&
    selectedPreviewContactIds.value.length === 0
  ) {
    previewError.value = t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.EMPTY_SELECTED');
    return;
  }

  if (isAudiencePreviewTruncated.value) {
    previewError.value = t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.TRUNCATED', {
      total: audiencePreviewTotalCount.value,
      limit: audiencePreviewLimit.value,
    });
    return;
  }

  emit('submit', prepareCampaignDetails());
};

onMounted(() => {
  store.dispatch('attributes/get');
  MailingListsAPI.get().then(response => {
    mailingLists.value = response.data?.payload || [];
  });

  isLoadingEmailMarketingTemplates.value = true;
  EmailMarketingTemplatesAPI.get()
    .then(response => {
      const payload = response.data?.payload ?? response.data ?? [];
      emailMarketingTemplates.value = Array.isArray(payload)
        ? payload
        : payload.email_marketing_templates || payload.templates || [];
      emailMarketingTemplatesError.value = '';
    })
    .catch(error => {
      emailMarketingTemplates.value = [];
      emailMarketingTemplatesError.value =
        error?.response?.data?.message ||
        t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.ERROR_LOAD');
    })
    .finally(() => {
      isLoadingEmailMarketingTemplates.value = false;
    });
});

watch(
  () => [
    state.emailCampaignKind,
    state.inboxId,
    state.selectedMailingLists.join(','),
    state.selectedContacts.join(','),
    state.selectedLabels.join(','),
    state.attributeKey,
    state.attributeOperator,
    state.attributeValues,
    state.attributeSelectedValues.join(','),
  ],
  resetAudiencePreview
);

watch(
  () => state.attributeKey,
  () => {
    state.attributeValues = '';
    state.attributeSelectedValues = [];
    state.attributeOperator = 'equal_to';
  }
);

watch(
  () => state.attributeOperator,
  operator => {
    if (operator === 'is_present') {
      state.attributeValues = '';
      state.attributeSelectedValues = [];
    }
  }
);

watch(selectedAttributeType, () => {
  state.attributeValues = '';
  state.attributeSelectedValues = [];
  state.attributeOperator = 'equal_to';
});

defineExpose({
  state,
  v$,
  previewError,
  loadAudiencePreview,
  togglePreviewContact,
  handleAttachmentsChange,
  insertVariable,
  handleTemplateSelection,
  isDirty,
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.LABEL') }}
      </label>
      <EmailMarketingTemplateSelector
        :model-value="state.emailMarketingTemplateId || ''"
        :templates="availableEmailMarketingTemplates"
        :aria-label="t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.LABEL')"
        :placeholder="
          t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.PLACEHOLDER')
        "
        :empty-label="t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.EMPTY')"
        :clear-label="t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.CLEAR')"
        :loading-label="t('CAMPAIGN.EMAIL.CREATE.FORM.SAVED_TEMPLATE.LOADING')"
        :is-loading="isLoadingEmailMarketingTemplates"
        :error="emailMarketingTemplatesError"
        @update:model-value="handleTemplateSelection"
      />
    </div>

    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <LiquidVariableAutocomplete
      v-model="state.subject"
      :options="liquidVariableOptions"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.PLACEHOLDER')"
    />
    <span v-if="formErrors.subject" class="text-xs text-n-ruby-10">
      {{ formErrors.subject }}
    </span>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.LABEL') }}
      </label>
      <div
        class="message-editor rounded-lg outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 bg-n-alpha-black2 px-3 py-3 [&_.ProseMirror-woot-style]:min-h-48 [&_.ProseMirror-woot-style]:max-h-[22rem] [&_.ProseMirror-menubar-wrapper]:gap-2 [&_.ProseMirror-menubar]:flex [&_.ProseMirror-menubar]:flex-wrap [&_.ProseMirror-menubar]:gap-3 [&_.ProseMirror-menubar]:border-b [&_.ProseMirror-menubar]:border-n-weak [&_.ProseMirror-menubar]:pb-2 [&_.ProseMirror-menuitem]:m-0"
        :class="{ editor_warning: formErrors.message }"
      >
        <WootMessageEditor
          v-model="state.message"
          editor-id="email-campaign-message"
          channel-type="Context::Default"
          enable-variables
          :aria-label="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.LABEL')"
          :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.PLACEHOLDER')"
          :focus-on-mount="false"
        />
      </div>
      <span v-if="formErrors.message" class="text-xs text-n-ruby-10">
        {{ formErrors.message }}
      </span>
      <span class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.HELP') }}
      </span>
      <div class="mt-2 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3">
        <div class="flex flex-col gap-3">
          <div class="min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.LABEL') }}
            </p>
            <p class="mt-1 text-xs leading-5 text-n-slate-10">
              {{ t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.HELP') }}
            </p>
          </div>

          <div class="grid grid-cols-1 items-end gap-3 md:grid-cols-[1fr_auto]">
            <div class="relative flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.SELECT_LABEL') }}
              </span>
              <button
                type="button"
                data-testid="email-variable-select"
                class="flex h-10 w-full items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-left text-sm text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand"
                :aria-expanded="showVariablePicker"
                @click="showVariablePicker = !showVariablePicker"
              >
                <span class="truncate">
                  {{
                    selectedVariableOption?.label ||
                    t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.PLACEHOLDER')
                  }}
                </span>
                <code
                  v-if="selectedVariableOption"
                  class="shrink-0 text-xs text-n-brand"
                >
                  {{ selectedVariableOption.token }}
                </code>
              </button>
              <div
                v-if="showVariablePicker"
                class="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 p-2 shadow-xl"
              >
                <div
                  v-for="group in variableGroups"
                  :key="group.key"
                  class="flex flex-col gap-1 pb-2 last:pb-0"
                >
                  <p
                    class="px-2 py-1 text-xs font-medium uppercase text-n-slate-10"
                  >
                    {{ group.label }}
                  </p>
                  <button
                    v-for="variable in group.options"
                    :key="variable.key"
                    type="button"
                    data-testid="email-variable-option"
                    class="rounded-md px-2 py-2 text-left hover:bg-n-alpha-3"
                    @click="selectedVariableToken = variable.token"
                  >
                    <span class="block truncate text-sm text-n-slate-12">
                      {{ variable.label }}
                    </span>
                    <code class="block truncate text-xs text-n-brand">
                      {{ variable.token }}
                    </code>
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              data-testid="email-variable-insert"
              class="inline-flex h-10 items-center justify-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white transition-colors hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:bg-n-alpha-3 disabled:text-n-slate-10"
              :disabled="!selectedVariableToken"
              @click="insertVariable(selectedVariableToken)"
            >
              {{ t('CAMPAIGN.EMAIL.CREATE.FORM.VARIABLES.BUTTON') }}
            </button>
          </div>
          <p
            v-if="selectedVariableOption"
            data-testid="email-variable-token-preview"
            class="rounded-md bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-10"
          >
            <span class="font-medium text-n-slate-12">
              {{ selectedVariableOption.label }}
            </span>
            <code class="ml-2 text-n-brand">
              {{ selectedVariableOption.token }}
            </code>
          </p>
        </div>
      </div>
    </div>

    <TextArea
      v-model="state.htmlContent"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.HTML_CONTENT.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.HTML_CONTENT.PLACEHOLDER')"
      show-character-count
    />

    <TextArea
      v-model="state.signature"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SIGNATURE.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SIGNATURE.PLACEHOLDER')"
      :message="t('CAMPAIGN.EMAIL.CREATE.FORM.SIGNATURE.HELP')"
      message-type="info"
    />

    <div class="flex flex-col gap-1">
      <label
        for="email-campaign-kind"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.KIND.LABEL') }}
      </label>
      <ComboBox
        id="email-campaign-kind"
        v-model="state.emailCampaignKind"
        :options="campaignKindOptions"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="email-attachments"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.LABEL') }}
      </label>
      <input
        id="email-attachments"
        ref="attachmentInput"
        type="file"
        multiple
        accept=".csv,.doc,.docx,.gif,.jpeg,.jpg,.pdf,.png,.ppt,.pptx,.rtf,.txt,.webp,.xls,.xlsx"
        class="block w-full text-sm text-n-slate-12 file:mr-3 file:rounded-md file:border-0 file:bg-n-alpha-2 file:px-3 file:py-2 file:text-sm file:font-medium file:text-n-slate-12 hover:file:bg-n-alpha-3"
        @change="handleAttachmentsChange"
      />
      <div class="flex min-h-5 items-center gap-2">
        <span class="text-xs text-n-slate-10">
          {{
            t('CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.SELECTED', {
              count: state.attachments.length,
            })
          }}
        </span>
        <button
          v-if="state.attachments.length || attachmentError"
          type="button"
          class="inline-flex size-5 items-center justify-center text-n-slate-10 hover:text-n-slate-12"
          :aria-label="t('CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.CLEAR')"
          :title="t('CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.CLEAR')"
          @click="clearAttachments"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
      <span class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ATTACHMENTS.HELP') }}
      </span>
      <span v-if="attachmentError" class="text-xs text-n-ruby-10">
        {{ attachmentError }}
      </span>
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="email-inbox"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="email-inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div v-if="requiresMailingList" class="flex flex-col gap-1">
      <label
        for="email-mailing-lists"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.MAILING_LISTS.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedMailingLists"
        :options="mailingListOptions"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.MAILING_LISTS.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.MAILING_LISTS.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div
      v-if="requiresMailingList"
      class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3 text-sm text-n-slate-11"
    >
      <p class="font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.CONSENT.LABEL') }}
      </p>
      <p class="mt-1 text-xs leading-5 text-n-slate-10">
        {{ consentFooterPreview }}
      </p>
      <div
        v-if="selectedMailingListDetails.length"
        class="mt-3 flex flex-col gap-2"
      >
        <div
          v-for="mailingList in selectedMailingListDetails"
          :key="mailingList.id"
          class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
        >
          <p class="text-xs font-medium text-n-slate-12">
            {{ mailingList.name }}
          </p>
          <p class="mt-1 text-xs leading-5 text-n-slate-10">
            {{
              mailingList.terms ||
              t('CAMPAIGN.EMAIL.CREATE.FORM.MAILING_LISTS.NO_TERMS')
            }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3 text-sm text-n-slate-11"
    >
      <p class="font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.LABEL') }}
      </p>
      <p class="mt-1 text-xs leading-5 text-n-slate-10">
        {{ trackingPreview }}
      </p>
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="email-contacts"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="selectedContactIds"
        :options="contactOptions"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.PLACEHOLDER')"
        :search-placeholder="
          t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.SEARCH_PLACEHOLDER')
        "
        :empty-state="
          isSearchingContacts
            ? t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.SEARCHING')
            : t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.EMPTY')
        "
        use-api-results
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
        @search="searchContacts"
      />
      <span class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.HELP') }}
      </span>
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="email-audience"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedLabels"
        :options="audienceList"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
      <div class="flex flex-col gap-1">
        <label
          for="email-contact-attribute"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.LABEL') }}
        </label>
        <ComboBox
          id="email-contact-attribute"
          v-model="state.attributeKey"
          :options="contactAttributeOptions"
          :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label
          for="email-contact-attribute-operator"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.OPERATOR_LABEL') }}
        </label>
        <ComboBox
          id="email-contact-attribute-operator"
          v-model="state.attributeOperator"
          :options="operatorOptions"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label
          for="email-contact-attribute-value"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.VALUE_LABEL') }}
        </label>
        <ComboBox
          v-if="
            isSelectedAttributeCheckbox &&
            state.attributeOperator !== 'is_present'
          "
          id="email-contact-attribute-value"
          v-model="state.attributeValues"
          :options="checkboxAttributeOptions"
          :placeholder="
            t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.BOOLEAN.PLACEHOLDER')
          "
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <TagMultiSelectComboBox
          v-else-if="
            isSelectedAttributeList && state.attributeOperator !== 'is_present'
          "
          v-model="state.attributeSelectedValues"
          :options="selectedAttributeListOptions"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.VALUE_LABEL')"
          :placeholder="
            t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.LIST_PLACEHOLDER')
          "
          class="[&>div>button]:bg-n-alpha-black2"
        />
        <Input
          v-else
          v-model="state.attributeValues"
          :placeholder="
            t('CAMPAIGN.EMAIL.CREATE.FORM.ATTRIBUTE.VALUE_PLACEHOLDER')
          "
          :disabled="state.attributeOperator === 'is_present'"
        />
      </div>
    </div>
    <span v-if="formErrors.audience" class="-mt-2 text-xs text-n-ruby-10">
      {{ formErrors.audience }}
    </span>

    <div
      class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3 flex flex-col gap-3"
    >
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.LABEL') }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.HELP') }}
          </p>
        </div>
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="
            isLoadingAudiencePreview
              ? t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.LOADING')
              : audiencePreviewActionLabel
          "
          :disabled="!canLoadAudiencePreview"
          @click="loadAudiencePreview"
        />
      </div>

      <p v-if="previewError" class="text-xs text-n-ruby-10">
        {{ previewError }}
      </p>

      <div v-if="hasAudiencePreview" class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="text-xs text-n-slate-11">
            {{ audiencePreviewSummary }}
          </p>
          <p
            v-if="blockedPreviewCount > 0"
            class="w-full text-xs text-n-amber-11"
          >
            {{ audiencePreviewEligibilitySummary }}
          </p>
          <p
            v-if="isAudiencePreviewTruncated"
            class="w-full text-xs text-n-ruby-10"
          >
            {{
              t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.TRUNCATED', {
                total: audiencePreviewTotalCount,
                limit: audiencePreviewLimit,
              })
            }}
          </p>
          <div class="flex gap-2">
            <button
              type="button"
              class="text-xs text-n-brand"
              @click="selectAllPreviewContacts"
            >
              {{ t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.SELECT_ALL') }}
            </button>
            <button
              type="button"
              class="text-xs text-n-slate-11"
              @click="clearPreviewContacts"
            >
              {{ t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.CLEAR_ALL') }}
            </button>
          </div>
        </div>

        <div
          v-if="audiencePreviewContacts.length"
          data-testid="email-audience-preview-list"
          class="max-h-72 overflow-y-auto rounded-lg border border-n-weak divide-y divide-n-weak"
        >
          <label
            v-for="contact in audiencePreviewContacts"
            :key="contact.id"
            class="flex items-start gap-3 px-3 py-2 text-sm"
            :class="{ 'opacity-60': isPreviewContactBlocked(contact) }"
          >
            <input
              type="checkbox"
              class="mt-1 h-4 w-4 rounded border-n-slate-6 text-n-brand"
              :checked="selectedPreviewContactIds.includes(contact.id)"
              :disabled="isPreviewContactBlocked(contact)"
              @change="togglePreviewContact(contact.id)"
            />
            <span class="min-w-0 flex-1">
              <span class="block truncate font-medium text-n-slate-12">
                {{ contact.name || contact.email }}
              </span>
              <span class="block truncate text-xs text-n-slate-10">
                {{ contact.email }}
              </span>
            </span>
            <span
              class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
            >
              {{ previewStatusLabel(contact) }}
            </span>
          </label>
        </div>
        <p
          v-else
          class="rounded-lg border border-dashed border-n-weak px-3 py-4 text-sm text-n-slate-11"
        >
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.PREVIEW.EMPTY') }}
        </p>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <details class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3">
      <summary class="cursor-pointer text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_TITLE') }}
      </summary>
      <p class="mt-2 text-xs leading-5 text-n-slate-10">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_HELP') }}
      </p>
      <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          v-model="state.utmSource"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_SOURCE')"
          placeholder="newsletter"
        />
        <Input
          v-model="state.utmMedium"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_MEDIUM')"
          placeholder="email"
        />
        <Input
          v-model="state.utmCampaign"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_CAMPAIGN')"
          placeholder="lancamento-julho"
        />
        <Input
          v-model="state.utmTerm"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_TERM')"
          placeholder="opcional"
        />
        <Input
          v-model="state.utmContent"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_CONTENT')"
          placeholder="botao-principal"
        />
      </div>
      <p v-if="!hasCompleteUtmValues" class="mt-2 text-xs text-n-ruby-10">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.TRACKING.UTM_ERROR') }}
      </p>
    </details>

    <div
      class="sticky bottom-0 z-10 -mx-3 mt-2 flex items-center justify-between w-[calc(100%+1.5rem)] gap-3 border-t border-n-weak bg-n-solid-2/95 px-3 py-3 backdrop-blur"
    >
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="submitButtonLabel"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
